import React, { useState, useEffect } from 'react'
import { useAuth } from '@/contexts/AuthContext'
import { 
  reportService, 
  ReportFilters, 
  TimeEntryDetail
} from '@/services/reportService'

interface EnhancedTimeEntriesDetailTableProps {
  filters: ReportFilters
}

type GroupMode = 'date' | 'project'

interface EnhancedTimeEntry extends TimeEntryDetail {
  percentage: number
  colorClass: string
}

interface GroupedData {
  date: {
    [date: string]: {
      entries: EnhancedTimeEntry[]
      totalHours: number
      totalDuration: number
    }
  }
  project: {
    [projectId: string]: {
      projectName: string
      projectColor: string
      entries: EnhancedTimeEntry[]
      totalHours: number
      totalDuration: number
    }
  }
}

// 檢視模式選擇器組件
const ViewModeSelector: React.FC<{
  mode: GroupMode
  onChange: (mode: GroupMode) => void
}> = ({ mode, onChange }) => {
  return (
    <div className="flex items-center gap-2 mb-4">
      <span className="text-sm font-medium text-secondary-700">檢視模式：</span>
      <div className="flex bg-secondary-100 rounded-lg p-1">
        <button
          onClick={() => onChange('date')}
          className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
            mode === 'date' 
              ? 'bg-white text-primary-600 shadow-sm' 
              : 'text-secondary-600 hover:text-secondary-800'
          }`}
        >
          📅 按日期群組
        </button>
        <button
          onClick={() => onChange('project')}
          className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
            mode === 'project' 
              ? 'bg-white text-primary-600 shadow-sm' 
              : 'text-secondary-600 hover:text-secondary-800'
          }`}
        >
          📁 按專案群組
        </button>
      </div>
    </div>
  )
}

// 百分比視覺化組件
const PercentageBar: React.FC<{
  percentage: number
  colorClass: string
}> = ({ percentage, colorClass }) => {
  return (
    <div className="flex items-center gap-2">
      <span className="text-sm font-medium min-w-[45px]">
        {percentage}%
      </span>
      <div className="flex-1 bg-secondary-200 rounded-full h-2 overflow-hidden">
        <div
          className={`h-full transition-all duration-300 ${colorClass}`}
          style={{ width: `${percentage}%` }}
        />
      </div>
    </div>
  )
}

// 手機版卡片組件
const MobileTimeEntryCard: React.FC<{
  entry: EnhancedTimeEntry
  showDate?: boolean
  showProject?: boolean
}> = ({ entry, showDate = false, showProject = true }) => {
  const { user } = useAuth()
  const isAdmin = user?.role === 'ADMIN' || user?.role === 'MANAGER'

  const getStatusDisplay = (status: string) => {
    switch (status) {
      case 'RUNNING':
      case 'STOPPED':
        return { label: '未提交', icon: '📝', color: 'text-gray-600 bg-gray-100' }
      case 'SUBMITTED':
        return { label: '已提交', icon: '📤', color: 'text-yellow-600 bg-yellow-100' }
      case 'APPROVED':
        return { label: '已核准', icon: '✅', color: 'text-green-600 bg-green-100' }
      case 'REJECTED':
        return { label: '已拒絕', icon: '❌', color: 'text-red-600 bg-red-100' }
      default:
        return { label: '未知', icon: '❓', color: 'text-gray-600 bg-gray-100' }
    }
  }

  const formatDuration = (seconds: number): string => {
    const hours = Math.floor(seconds / 3600)
    const minutes = Math.round((seconds % 3600) / 60)
    
    if (minutes === 0) {
      return `${hours}h`
    }
    return `${hours}h ${minutes}m`
  }

  const statusDisplay = getStatusDisplay(entry.status)

  return (
    <div className="bg-white rounded-lg shadow-sm border border-secondary-200 p-4 mb-3">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          {showProject && (
            <>
              <div
                className="w-3 h-3 rounded-full"
                style={{ backgroundColor: entry.projectColor }}
              />
              <span className="font-medium text-secondary-900">{entry.projectName}</span>
            </>
          )}
          {showDate && (
            <span className="text-sm text-secondary-600">{entry.date}</span>
          )}
        </div>
        <span className={`px-2 py-1 rounded text-xs font-medium ${entry.colorClass}`}>
          {entry.percentage}%
        </span>
      </div>
      
      <div className="text-sm text-secondary-700 mb-2">
        {entry.description || '無描述'}
      </div>
      
      <div className="flex justify-between items-center text-xs text-secondary-500 mb-2">
        <span>{entry.startTime} - {entry.endTime}</span>
        <span>{formatDuration(entry.duration)}</span>
      </div>

      <div className="flex items-center justify-between mb-2">
        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${statusDisplay.color}`}>
          <span className="mr-1">{statusDisplay.icon}</span>
          {statusDisplay.label}
        </span>
        {isAdmin && entry.userName && (
          <span className="text-xs text-secondary-500">{entry.userName}</span>
        )}
      </div>
      
      <div className="mt-2">
        <PercentageBar percentage={entry.percentage} colorClass={entry.colorClass} />
      </div>
    </div>
  )
}

const EnhancedTimeEntriesDetailTable: React.FC<EnhancedTimeEntriesDetailTableProps> = ({ filters }) => {
  const { user } = useAuth()
  const isAdmin = user?.role === 'ADMIN' || user?.role === 'MANAGER'

  const [groupMode, setGroupMode] = useState<GroupMode>('date')
  const [groupedData, setGroupedData] = useState<GroupedData>({ date: {}, project: {} })
  const [loading, setLoading] = useState(false)
  const [hasMore, setHasMore] = useState(false)
  const [totalCount, setTotalCount] = useState(0)
  const [offset, setOffset] = useState(0)
  const [error, setError] = useState<string | null>(null)

  const LIMIT = 50

  // 獲取百分比顏色類別
  const getPercentageColorClass = (percentage: number): string => {
    if (percentage > 40) return 'bg-primary-600 text-white'
    if (percentage > 25) return 'bg-primary-500 text-white'
    if (percentage > 15) return 'bg-primary-400 text-white'
    if (percentage > 5) return 'bg-primary-300 text-primary-900'
    return 'bg-primary-200 text-primary-800'
  }

  // 計算日期群組百分比
  const calculateDateGroupPercentage = (entry: TimeEntryDetail, dailyTotal: number): number => {
    return Number(((entry.duration / dailyTotal) * 100).toFixed(1))
  }

  // 計算專案群組百分比
  const calculateProjectGroupPercentage = (entry: TimeEntryDetail, projectTotal: number): number => {
    return Number(((entry.duration / projectTotal) * 100).toFixed(1))
  }

  // 處理時間項目數據
  const processTimeEntries = (entries: TimeEntryDetail[]): GroupedData => {
    const dateGroups: GroupedData['date'] = {}
    const projectGroups: GroupedData['project'] = {}

    // 按日期分組
    entries.forEach(entry => {
      const date = entry.date
      if (!dateGroups[date]) {
        dateGroups[date] = {
          entries: [],
          totalHours: 0,
          totalDuration: 0
        }
      }
      // 暫時將原始entry添加到陣列中，後面會計算百分比和顏色
      dateGroups[date].entries.push(entry as EnhancedTimeEntry)
      dateGroups[date].totalDuration += entry.duration
      dateGroups[date].totalHours = Number((dateGroups[date].totalDuration / 3600).toFixed(1))
    })

    // 按專案分組
    entries.forEach(entry => {
      const projectId = entry.projectId
      if (!projectGroups[projectId]) {
        projectGroups[projectId] = {
          projectName: entry.projectName,
          projectColor: entry.projectColor,
          entries: [],
          totalHours: 0,
          totalDuration: 0
        }
      }
      // 暫時將原始entry添加到陣列中，後面會計算百分比和顏色
      projectGroups[projectId].entries.push(entry as EnhancedTimeEntry)
      projectGroups[projectId].totalDuration += entry.duration
      projectGroups[projectId].totalHours = Number((projectGroups[projectId].totalDuration / 3600).toFixed(1))
    })

    // 計算百分比和顏色
    Object.values(dateGroups).forEach(group => {
      group.entries = group.entries.map(entry => {
        const percentage = calculateDateGroupPercentage(entry, group.totalDuration)
        return {
          ...entry,
          percentage,
          colorClass: getPercentageColorClass(percentage)
        }
      })
    })

    Object.values(projectGroups).forEach(group => {
      group.entries = group.entries.map(entry => {
        const percentage = calculateProjectGroupPercentage(entry, group.totalDuration)
        return {
          ...entry,
          percentage,
          colorClass: getPercentageColorClass(percentage)
        }
      })
    })

    return { date: dateGroups, project: projectGroups }
  }

  // 載入時間項目明細
  const loadTimeEntries = async (isLoadMore: boolean = false) => {
    try {
      setLoading(true)
      setError(null)

      const currentOffset = isLoadMore ? offset : 0
      const result = await reportService.getTimeEntriesDetail(filters, currentOffset, LIMIT)

      let allEntries: TimeEntryDetail[] = []
      
      if (isLoadMore) {
        // 獲取現有的所有條目
        const existingEntries = [
          ...Object.values(groupedData.date).flatMap(group => group.entries),
          ...Object.values(groupedData.project).flatMap(group => group.entries)
        ]
        
        // 去重並合併
        const existingIds = new Set(existingEntries.map(entry => entry.id))
        const newEntries = result.entries.filter(entry => !existingIds.has(entry.id))
        allEntries = [...existingEntries, ...newEntries]
        
        setOffset(currentOffset + newEntries.length)
      } else {
        allEntries = result.entries
        setOffset(result.entries.length)
      }

      const newGroupedData = processTimeEntries(allEntries)
      setGroupedData(newGroupedData)
      setHasMore(result.hasMore)
      setTotalCount(result.totalCount)
    } catch (error) {
      console.error('Failed to load time entries detail:', error)
      setError(error instanceof Error ? error.message : '載入明細失敗')
    } finally {
      setLoading(false)
    }
  }

  // 當篩選條件改變時重新載入
  useEffect(() => {
    loadTimeEntries(false)
  }, [filters])

  // 當模式切換時重新處理數據
  useEffect(() => {
    // 模式切換不需要重新載入數據，只需要重新顯示
  }, [groupMode])

  // 載入更多
  const handleLoadMore = () => {
    if (!loading && hasMore) {
      loadTimeEntries(true)
    }
  }

  // 格式化日期顯示
  const formatDateDisplay = (dateStr: string): string => {
    const weekdays = ['星期日', '星期一', '星期二', '星期三', '星期四', '星期五', '星期六']
    
    const dateParts = dateStr.split('-')
    if (dateParts.length === 3) {
      const year = parseInt(dateParts[0])
      const month = parseInt(dateParts[1])
      const day = parseInt(dateParts[2])
      
      const date = new Date(year, month - 1, day)
      const weekday = weekdays[date.getDay()]
      
      return `${year}年${month}月${day}日 (${weekday})`
    }
    
    const date = new Date(dateStr + 'T00:00:00')
    const year = date.getFullYear()
    const month = date.getMonth() + 1
    const day = date.getDate()
    const weekday = weekdays[date.getDay()]
    
    return `${year}年${month}月${day}日 (${weekday})`
  }

  // 格式化工時顯示
  const formatDuration = (seconds: number): string => {
    const hours = Math.floor(seconds / 3600)
    const minutes = Math.round((seconds % 3600) / 60)
    
    if (minutes === 0) {
      return `${hours}h`
    }
    return `${hours}h ${minutes}m`
  }

  // 獲取狀態顯示
  const getStatusDisplay = (status: string) => {
    switch (status) {
      case 'RUNNING':
      case 'STOPPED':
        return { label: '未提交', icon: '📝', color: 'text-gray-600 bg-gray-100' }
      case 'SUBMITTED':
        return { label: '已提交', icon: '📤', color: 'text-yellow-600 bg-yellow-100' }
      case 'APPROVED':
        return { label: '已核准', icon: '✅', color: 'text-green-600 bg-green-100' }
      case 'REJECTED':
        return { label: '已拒絕', icon: '❌', color: 'text-red-600 bg-red-100' }
      default:
        return { label: '未知', icon: '❓', color: 'text-gray-600 bg-gray-100' }
    }
  }

  // 渲染日期群組檢視
  const renderDateGroupView = () => {
    const sortedDates = Object.keys(groupedData.date).sort((a, b) => b.localeCompare(a))

    return (
      <div className="space-y-4">
        {sortedDates.map(date => {
          const group = groupedData.date[date]
          
          return (
            <div key={date} className="card">
              {/* 日期標題 */}
              <div className="px-6 py-4 bg-secondary-50 border-b border-secondary-200">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-medium text-secondary-800">
                    📅 {formatDateDisplay(date)}
                  </h3>
                  <div className="text-sm font-medium text-secondary-600">
                    總計 {group.totalHours} 小時
                  </div>
                </div>
              </div>

              {/* 桌面版表格 */}
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-secondary-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                        專案
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                        工作描述
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                        時間
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                        工時
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                        佔比
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                        狀態
                      </th>
                      {isAdmin && (
                        <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                          用戶
                        </th>
                      )}
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-secondary-200">
                    {group.entries.map(entry => {
                      const statusDisplay = getStatusDisplay(entry.status)
                      
                      return (
                        <tr key={entry.id} className="hover:bg-secondary-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <div
                                className="w-3 h-3 rounded-full mr-3"
                                style={{ backgroundColor: entry.projectColor }}
                              ></div>
                              <span className="text-sm font-medium text-secondary-900">
                                {entry.projectName}
                              </span>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="text-sm text-secondary-900 max-w-xs truncate">
                              {entry.description || '無描述'}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-secondary-900">
                            {entry.startTime} - {entry.endTime}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-secondary-900">
                            {formatDuration(entry.duration)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="w-32">
                              <PercentageBar percentage={entry.percentage} colorClass={entry.colorClass} />
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusDisplay.color}`}>
                              <span className="mr-1">{statusDisplay.icon}</span>
                              {statusDisplay.label}
                            </span>
                          </td>
                          {isAdmin && (
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-secondary-900">
                              {entry.userName || '-'}
                            </td>
                          )}
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>

              {/* 手機版卡片 */}
              <div className="md:hidden p-4">
                {group.entries.map(entry => (
                  <MobileTimeEntryCard
                    key={entry.id}
                    entry={entry}
                    showProject={true}
                    showDate={false}
                  />
                ))}
              </div>
            </div>
          )
        })}
      </div>
    )
  }

  // 渲染專案群組檢視
  const renderProjectGroupView = () => {
    const sortedProjects = Object.keys(groupedData.project).sort((a, b) => 
      groupedData.project[b].totalDuration - groupedData.project[a].totalDuration
    )

    return (
      <div className="space-y-4">
        {sortedProjects.map(projectId => {
          const group = groupedData.project[projectId]
          
          return (
            <div key={projectId} className="card">
              {/* 專案標題 */}
              <div className="px-6 py-4 bg-secondary-50 border-b border-secondary-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div
                      className="w-4 h-4 rounded-full mr-3"
                      style={{ backgroundColor: group.projectColor }}
                    ></div>
                    <h3 className="text-lg font-medium text-secondary-800">
                      📁 {group.projectName}
                    </h3>
                  </div>
                  <div className="text-sm font-medium text-secondary-600">
                    總計 {group.totalHours} 小時
                  </div>
                </div>
              </div>

              {/* 桌面版表格 */}
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-secondary-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                        日期
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                        工作描述
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                        時間
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                        工時
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                        佔比
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                        狀態
                      </th>
                      {isAdmin && (
                        <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                          用戶
                        </th>
                      )}
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-secondary-200">
                    {group.entries
                      .sort((a, b) => b.date.localeCompare(a.date))
                      .map(entry => {
                        const statusDisplay = getStatusDisplay(entry.status)
                        
                        return (
                          <tr key={entry.id} className="hover:bg-secondary-50">
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-secondary-900">
                              {formatDateDisplay(entry.date)}
                            </td>
                            <td className="px-6 py-4">
                              <div className="text-sm text-secondary-900 max-w-xs truncate">
                                {entry.description || '無描述'}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-secondary-900">
                              {entry.startTime} - {entry.endTime}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-secondary-900">
                              {formatDuration(entry.duration)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="w-32">
                                <PercentageBar percentage={entry.percentage} colorClass={entry.colorClass} />
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusDisplay.color}`}>
                                <span className="mr-1">{statusDisplay.icon}</span>
                                {statusDisplay.label}
                              </span>
                            </td>
                            {isAdmin && (
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-secondary-900">
                                {entry.userName || '-'}
                              </td>
                            )}
                          </tr>
                        )
                      })}
                  </tbody>
                </table>
              </div>

              {/* 手機版卡片 */}
              <div className="md:hidden p-4">
                {group.entries
                  .sort((a, b) => b.date.localeCompare(a.date))
                  .map(entry => (
                    <MobileTimeEntryCard
                      key={entry.id}
                      entry={entry}
                      showProject={false}
                      showDate={true}
                    />
                  ))}
              </div>
            </div>
          )
        })}
      </div>
    )
  }

  if (loading && Object.keys(groupedData.date).length === 0 && Object.keys(groupedData.project).length === 0) {
    return (
      <div className="card p-8">
        <div className="flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
          <span className="ml-3 text-secondary-600">載入明細中...</span>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="card p-6 bg-red-50 border-red-200">
        <div className="flex items-center space-x-2">
          <span className="text-red-600">⚠️</span>
          <span className="text-red-800">{error}</span>
          <button
            onClick={() => loadTimeEntries(false)}
            className="ml-auto btn-secondary text-sm"
          >
            重試
          </button>
        </div>
      </div>
    )
  }

  const hasData = Object.keys(groupedData.date).length > 0 || Object.keys(groupedData.project).length > 0

  if (!hasData) {
    return (
      <div className="card p-8">
        <div className="text-center text-secondary-600">
          <div className="text-4xl mb-4">📋</div>
          <h3 className="text-lg font-medium mb-2">無工作項目明細</h3>
          <p>在選定的時間範圍內沒有找到任何工作記錄</p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* 標題和模式選擇器 */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <h2 className="text-xl font-semibold text-secondary-800">📋 工作項目明細</h2>
        <div className="flex items-center justify-between">
          <div className="text-sm text-secondary-600">
            共 {totalCount} 筆記錄
          </div>
        </div>
      </div>

      <ViewModeSelector mode={groupMode} onChange={setGroupMode} />

      {/* 根據模式渲染不同檢視 */}
      {groupMode === 'date' ? renderDateGroupView() : renderProjectGroupView()}

      {/* 載入更多按鈕 */}
      {hasMore && (
        <div className="flex justify-center">
          <button
            onClick={handleLoadMore}
            disabled={loading}
            className="btn-secondary flex items-center space-x-2"
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-secondary-600"></div>
                <span>載入中...</span>
              </>
            ) : (
              <>
                <span>🔄</span>
                <span>載入更多</span>
              </>
            )}
          </button>
        </div>
      )}

      {/* 載入狀態提示 */}
      {!hasMore && hasData && (
        <div className="text-center text-sm text-secondary-500 py-4">
          已顯示全部 {totalCount} 筆記錄
        </div>
      )}
    </div>
  )
}

export default EnhancedTimeEntriesDetailTable
